/* Offline order queue + reference-data cache.
 *
 * Two independent concerns:
 *  1. cacheRef / getCachedRef — persist the last-known menu, stations, categories,
 *     bill-settings so the POS can still render a full order screen offline.
 *  2. enqueueOrder / drainQueue — hold new orders locally when offline and
 *     replay them to the backend when connectivity returns.
 *
 * Both use plain localStorage — small (< 5 MB) but simple, sync, and works in every
 * browser without extra deps. An entire shift of ~500 orders comfortably fits.
 */
import axios from "axios";
import { API } from "./pos";

const REF_KEY = "qoh_ref_v1";        // Cached menu / stations / etc.
const QUEUE_KEY = "qoh_offline_q_v1"; // Queued orders awaiting sync

/* -----------------------------------------------------------
 * Reference cache — menu, stations, categories, bill settings
 * --------------------------------------------------------- */
export function cacheRef(patch) {
  try {
    const cur = getCachedRef() || {};
    const next = { ...cur, ...patch, __ts: new Date().toISOString() };
    localStorage.setItem(REF_KEY, JSON.stringify(next));
  } catch (_) { /* quota / private-mode → silently ignore */ }
}

export function getCachedRef() {
  try {
    const raw = localStorage.getItem(REF_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

/* -----------------------------------------------------------
 * Offline queue
 * --------------------------------------------------------- */

/** Wire up an ISO timestamp + a client-side local token so KOT/Bill printing
 *  works while offline. Real order_number + token_number come from the server on sync. */
export function enqueueOrder(payload) {
  const q = readQueue();
  const localTokenSeq = q.length + 1; // simple ascending local number per-session
  const record = {
    local_id: `off_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    queued_at: new Date().toISOString(),
    payload,
    // These are placeholders shown to the customer on the offline receipt.
    // The server assigns real numbers on sync; the local receipt reads "OFFLINE" to be transparent.
    local_token_number: `OFF-${localTokenSeq}`,
    local_order_number: `OFF-${Date.now().toString().slice(-4)}`,
    retries: 0,
    last_error: null,
  };
  q.push(record);
  writeQueue(q);
  return record;
}

export function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
}

export function queueSize() { return readQueue().length; }

function writeQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch (_) { /* ignore */ }
}

/** Replay every queued order to the server, oldest first. Removes items that succeed;
 *  keeps items that fail (with a retry counter). Returns a summary. */
export async function drainQueue() {
  const q = readQueue();
  if (!q.length) return { synced: 0, failed: 0, remaining: 0 };
  const remaining = [];
  let synced = 0, failed = 0;
  for (const rec of q) {
    try {
      const r = await axios.post(`${API}/orders`, rec.payload);
      // Success — attach server IDs to a small "recently synced" log for the UI.
      pushSyncedLog({
        local_id: rec.local_id,
        server_id: r.data && r.data.id,
        order_number: r.data && r.data.order_number,
        token_number: r.data && r.data.token_number,
        synced_at: new Date().toISOString(),
      });
      synced += 1;
    } catch (err) {
      failed += 1;
      rec.retries = (rec.retries || 0) + 1;
      rec.last_error = (err && err.response && err.response.data && err.response.data.detail) || err.message || "sync failed";
      // Cap retries: after 10 failures, keep in queue but note the error — human intervention needed.
      remaining.push(rec);
    }
  }
  writeQueue(remaining);
  return { synced, failed, remaining: remaining.length };
}

const SYNC_LOG_KEY = "qoh_synced_log_v1";
function pushSyncedLog(entry) {
  try {
    const cur = JSON.parse(localStorage.getItem(SYNC_LOG_KEY) || "[]");
    cur.unshift(entry);
    localStorage.setItem(SYNC_LOG_KEY, JSON.stringify(cur.slice(0, 50))); // keep last 50
  } catch (_) { /* ignore */ }
}

export function readSyncedLog() {
  try { return JSON.parse(localStorage.getItem(SYNC_LOG_KEY) || "[]"); } catch (_) { return []; }
}
